/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * MUI theme for the inbox panes.
 *
 * The inbox is built from plain MUI controls (lists, Autocomplete, the Quill
 * toolbar) that look wrong under the Material Dashboard theme, so it keeps its
 * own theme as before. Unlike the old one it follows the app's dark mode:
 * light values come from styles/tokens, dark surfaces from the dark theme's
 * palette, so the inbox is no longer a white card on a dark page.
 */

import { alpha, createTheme } from '@mui/material/styles';
import colors from 'styles/tokens/colors';
import darkColors from 'assets/theme-dark/base/colors';

const DISPLAY_FONT = '"Sora", "Inter", "Helvetica", "Arial", sans-serif';
const BODY_FONT = '"Hanken Grotesk", "Inter", "Helvetica", "Arial", sans-serif';

export function createInboxTheme(darkMode) {
  // Dark mode uses the dark theme's light primary: the brand blue is about
  // 2.5:1 on the dark paper, too low for links, icons and counts.
  const primary = darkMode ? darkColors.primary.main : colors.brand.primary;
  const palette = darkMode
    ? {
        mode: 'dark',
        primary: { main: primary, contrastText: darkColors.background.default },
        background: { default: darkColors.background.default, paper: darkColors.background.paper },
        text: { primary: darkColors.text.main, secondary: darkColors.text.secondary },
        divider: alpha(darkColors.text.secondary, 0.18),
        error: { main: colors.callCenter.danger },
        success: { main: colors.success.main },
      }
    : {
        mode: 'light',
        primary: { main: primary, contrastText: colors.background.white },
        background: { default: colors.background.offWhite, paper: colors.background.white },
        text: { primary: colors.text.dark, secondary: colors.text.light },
        divider: colors.callCenter.cardBorder,
        error: { main: colors.error.text },
        success: { main: colors.success.main },
      };

  return createTheme({
    palette,
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: BODY_FONT,
      fontSize: 14,
      h5: { fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: '1.375rem' },
      h6: { fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: '1.125rem' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            '&.Mui-selected': {
              backgroundColor: darkMode ? alpha(primary, 0.24) : colors.brand.primaryContainer,
              color: darkMode ? theme.palette.text.primary : colors.brand.onPrimaryContainer,
            },
            '&.Mui-selected:hover': {
              backgroundColor: darkMode ? alpha(primary, 0.32) : alpha(primary, 0.18),
            },
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { fontSize: '0.75rem' } },
      },
    },
  });
}

export default createInboxTheme;
